import type { SchemaProperty, PropertyDataType } from 'types/schema';
import type { RenderMode, RendererRef } from 'types/aspect';
import { TYPE_DEFAULT_RENDERERS } from '@center-platform/component-catalog';
import { isLegacyComponentRegistered } from '../components/DesignSystemComponentRenderer';
import type { ValueAccessor, ChangeMapper } from '../components/DesignSystemComponentRenderer';
import { hasAdapter } from '../composition-host/adapterRegistry';

// The legacy-compat step below asks the adapter registry whether a name is mountable.
// `hasAdapter` itself ensures the built-ins are registered on first lookup — no eager
// module-scope call needed (see `adapterRegistry.ts`'s `ensureBuiltIns`).

/**
 * Legacy `editComponentProps`/`displayComponentProps` carry `valueAccessor`/`changeMapper`
 * directives (incl. `{type:'multiField'}`) that a plain `{value,onChange}` contract cannot
 * express — `UnifiedPropertyRendererMolecule` needs these verbatim to preserve `DesignSystemComponentRenderer`'s
 * multi-field fan-out semantics (EAINT-855 spec §Resolution chain step 3, gap 5).
 */
export interface LegacyShim {
  valueAccessor?: ValueAccessor;
  changeMapper?: ChangeMapper;
  componentProps?: Record<string, unknown>;
}

export interface ResolvedRenderer {
  /** Absent only when `isEmptyAffordance` is true — the terminal step renders localized text, not a component. */
  ref?: RendererRef;
  source: 'explicit' | 'legacy' | 'typeDefault' | 'terminal';
  /** Set only when `source === 'legacy'` and the resolved name came from `editComponent`/`displayComponent`. */
  legacyShim?: LegacyShim;
  /** True only when the chain bottoms out at the localized "no value" affordance (mode `empty`, nothing else declared). */
  isEmptyAffordance?: boolean;
}

const TERMINAL_EDIT: RendererRef = { origin: 'catalog', name: 'TextField' };
const TERMINAL_VIEW: RendererRef = { origin: 'catalog', name: 'GenericDisplay' };

/**
 * Whether a legacy `editComponent`/`displayComponent`/`component` NAME can actually be mounted by
 * something — either `DesignSystemComponentRenderer`'s `componentMap` (the shim path) or a
 * registered composition-host adapter (the catalog path). An unregistered name must NOT win chain
 * step 3: `DesignSystemComponentRenderer` returns `null` for a name it doesn't know and
 * `CatalogAdapterHost` degrades to the terminal default, so resolving to it would render the
 * property as an EMPTY CELL — exactly what the spec's "never blank" edge-case rule forbids
 * (specs/aspect-default-components.md §Edge cases). Several seeded schemas name components that
 * live in neither registry (e.g. `center_user.user_type`'s `editComponent: 'segmented-control'`,
 * `center_user.title`'s `'InputAtom'`/`'TextDisplayAtom'`), and those properties must keep
 * rendering through their per-data-type default.
 */
function isLegacyNameRenderable(name: string): boolean {
  if (isLegacyComponentRegistered(name)) return true;
  if (hasAdapter(name)) return true;
  // eslint-disable-next-line no-console
  console.warn(
    `[resolveRenderer] legacy component name "${name}" is registered in neither DesignSystemComponentRenderer nor the adapter registry — falling through to the per-data-type default`
  );
  return false;
}

/**
 * The one shared fallback chain every aspect/property render surface consumes (EAINT-855 spec
 * §Resolution chain, normative). Steps, in order:
 *   1. `property.renderers[mode]` — explicit ref for this exact mode.
 *   2. Mode degradation — `compact`/`empty` fall back to the explicit `view` ref (an explicit
 *      `compact`/`empty` ref already returned above), then CONTINUE. `compact` also reads the
 *      `view` column of the type-default table at step 4; `empty` does not, because its terminal
 *      step (the localized "no value" affordance) is the better answer for an absent value.
 *   3. Legacy compat — `editComponent` (edit) / `displayComponent` (else), then bare `component`.
 *   4. `TYPE_DEFAULT_RENDERERS[property.type][effectiveMode]` — the single code-defined table.
 *   5. Terminal — edit -> `TextField`; view/compact -> `GenericDisplay`; empty -> the localized
 *      "no value" affordance (no component at all).
 */
export function resolveRenderer(property: SchemaProperty, mode: RenderMode): ResolvedRenderer {
  const explicit = property.renderers?.[mode];
  if (explicit) return { ref: explicit, source: 'explicit' };

  if (mode === 'compact' || mode === 'empty') {
    const viewExplicit = property.renderers?.view;
    if (viewExplicit) return { ref: viewExplicit, source: 'explicit' };
  }

  const primaryLegacyName = mode === 'edit' ? property.editComponent : property.displayComponent;
  if (primaryLegacyName && isLegacyNameRenderable(primaryLegacyName)) {
    const componentProps = mode === 'edit' ? property.editComponentProps : property.displayComponentProps;
    return {
      ref: { origin: 'catalog', name: primaryLegacyName },
      source: 'legacy',
      legacyShim: {
        valueAccessor: componentProps?.valueAccessor as ValueAccessor | undefined,
        changeMapper: componentProps?.changeMapper as ChangeMapper | undefined,
        componentProps,
      },
    };
  }
  if (property.component && isLegacyNameRenderable(property.component)) {
    return { ref: { origin: 'catalog', name: property.component }, source: 'legacy' };
  }

  // `TYPE_DEFAULT_RENDERERS` is keyed on `view`/`edit` only, so `compact` continues its
  // degradation into the table and takes the VIEW default (spec §Resolution chain step 2, "then
  // continue"). Without this a `reference`/`array`/`date` property in compact mode skipped its
  // structured default and fell to terminal `GenericDisplay`, rendering a bare sphere id or
  // `["a","b"]`. `empty` deliberately does NOT degrade here: its terminal step is the localized
  // "no value" affordance, which beats rendering a view component over an absent value.
  const typeDefaultMode: RenderMode = mode === 'compact' ? 'view' : mode;
  const typeDefault = TYPE_DEFAULT_RENDERERS[property.type as PropertyDataType]?.[typeDefaultMode];
  if (typeDefault) return { ref: typeDefault, source: 'typeDefault' };

  if (mode === 'empty') return { source: 'terminal', isEmptyAffordance: true };
  return { ref: mode === 'edit' ? TERMINAL_EDIT : TERMINAL_VIEW, source: 'terminal' };
}
